import { createContext, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  fetchExtrajudicialNotification,
  fetchUpdateNotificationAddress,
  fetchUpdateNotificationDecision,
  resetNotificationAddressState,
  setEjn,
  setEjnId,
} from '../../store/reducers/ejn-selected';
import { type BaseFetchProps, defaultBaseFetchProps } from '../../store/utils';
import type {
  Address,
  ExtrajudicialNotification,
  NotificationDecision,
} from '../../types';
import type { Maybe, MaybeString } from '../../utils/types/maybe';
import { useEjnList } from './EjnListProvider';

interface IEjnSelectedContext {
  ejn: Maybe<ExtrajudicialNotification>;
  ejnId: MaybeString;
  loading: boolean;
  setEjnId: (newId: MaybeString) => void;
  decide: (decision: NotificationDecision) => void;
  decision: BaseFetchProps;
  updateAddress: (address: Address) => void;
  notificationAddress: BaseFetchProps;
  resetEditingAddress: () => void;
}

const defaultEjnSelectedContext: IEjnSelectedContext = {
  ejn: undefined,
  ejnId: undefined,
  loading: false,
  setEjnId: () => undefined,
  decide: () => undefined,
  decision: { ...defaultBaseFetchProps },
  updateAddress: () => undefined,
  notificationAddress: { ...defaultBaseFetchProps },
  resetEditingAddress: () => undefined,
};

const EjnSelectedContext = createContext(defaultEjnSelectedContext);

export const EjnSelectedProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const { ejn, ejnId, notificationId, loading, notificationAddress, decision } =
    useSelector(
      (state: RootState) => state.extrajudicialNotificationSelectedSlice,
    );

  const { extrajudicialNotifications, updateEjnState } = useEjnList();

  useEffect(() => {
    const foundEjn = extrajudicialNotifications.find((it) => it.id === ejnId);
    if (!foundEjn && ejnId) {
      dispatch(fetchExtrajudicialNotification(ejnId));
    }
    dispatch(setEjn(foundEjn));
  }, [extrajudicialNotifications, ejnId, dispatch]);

  useEffect(() => {
    if (ejn) {
      updateEjnState(ejn);
    }
  }, [ejn, updateEjnState]);

  const setSelectedId = useCallback(
    (newId: MaybeString) => {
      dispatch(setEjnId(newId));
    },
    [dispatch],
  );

  const updateAddress = (address: Address) => {
    if (notificationId && address) {
      dispatch(fetchUpdateNotificationAddress({ notificationId, address }));
    }
  };

  const decide = (decision: NotificationDecision) => {
    if (notificationId) {
      dispatch(fetchUpdateNotificationDecision({ notificationId, decision }));
    }
  };

  const resetEditingAddress = () => dispatch(resetNotificationAddressState());

  return (
    <EjnSelectedContext.Provider
      value={{
        ejn,
        ejnId,
        loading,
        setEjnId: setSelectedId,
        decide,
        decision,
        updateAddress,
        notificationAddress,
        resetEditingAddress,
      }}
    >
      {children}
    </EjnSelectedContext.Provider>
  );
};

export const useEjnSelected = () => {
  return useContext(EjnSelectedContext);
};
